import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attachHereMap, detachHereMap } from '../lib/hereMap';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { DriverOnboarding } from './DriverOnboarding';
import { 
  Package, MapPin, Navigation, User, CheckCircle, Route,
  AlertTriangle, Star, Truck, Weight, LogOut, Camera, X, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || '';
const HERE_API_KEY = process.env.REACT_APP_HERE_API_KEY;

// Dark theme delivery card
const DeliveryCard = ({ order, isActive, onSelect, onStartDelivery, onMarkDelivered }) => {
  const isTransit = order.status === 'in_transit';
  const isAssigned = order.status === 'assigned';
  
  return (
    <div 
      className={`bg-gray-900/80 rounded-xl p-4 border-l-4 cursor-pointer transition-all ${
        isActive ? 'ring-2 ring-emerald-500' : ''
      } ${isTransit ? 'border-emerald-500' : 'border-blue-500'}`}
      onClick={onSelect}
      data-testid={`delivery-card-${order.id}`}
    >
      {/* Order ID and Status */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-emerald-400 font-mono text-sm">#{order.id.slice(0, 6).toUpperCase()}</span>
        <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
          isTransit ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
        }`}>
          {isTransit ? 'Transit' : 'Assigned'}
        </span>
      </div>
      
      {/* Address */}
      <h3 className="text-white font-semibold text-lg mb-2">{order.delivery_address.split(',')[0]}</h3>
      
      {/* Business and Weight */}
      <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
        <Package className="w-4 h-4" />
        <span>{order.customer_name}</span>
        <span className="text-gray-600">·</span>
        <span className="flex items-center gap-1">
          <Weight className="w-3 h-3" />
          {order.total_weight} kg
        </span>
      </div>
      
      {/* Divider */}
      <div className="border-t border-gray-700 my-3" />
      
      {/* Customer and Delivery Type */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-500" />
          <span className="text-gray-300 text-sm">{order.customer_name}</span>
        </div>
        <span className={`text-sm font-medium ${
          order.order_value > 200 ? 'text-emerald-400' : 'text-blue-400'
        }`}>
          {order.order_value > 200 ? 'Express' : 'Standard'}
        </span>
      </div>
    </div>
  );
};

// Full screen map component
const DriverMap = ({ driverLocation, selectedOrder, orders }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    
    const initMap = () => {
      if (typeof window.H === 'undefined') return;
      
      try {
        const center = { lat: driverLocation?.lat || -33.8688, lng: driverLocation?.lng || 151.2093 };
        
        const map = attachHereMap('driver', mapRef.current, (element) => {
          const platform = new window.H.service.Platform({ apikey: HERE_API_KEY });
          const defaultLayers = platform.createDefaultLayers();
          
          const newMap = new window.H.Map(
            element,
            defaultLayers.vector.normal.map,
            { center, zoom: 14, pixelRatio: window.devicePixelRatio || 1 }
          );
          
          new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(newMap));
          return newMap;
        });
        // A map kept from an earlier visit still shows where it was left
        map.setCenter(center);
        map.setZoom(14);
        
        mapInstanceRef.current = map;
        setMapReady(true);
      } catch (err) {
        console.error('Map init error:', err);
      }
    };
    
    const timeoutId = setTimeout(initMap, 500);
    
    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) detachHereMap('driver');
      mapInstanceRef.current = null;
    };
  }, []);
  
  // Update markers when data changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    
    // Clear existing objects
    map.removeObjects(map.getObjects());
    
    // Add driver marker
    if (driverLocation?.lat && driverLocation?.lng) {
      const driverIcon = new window.H.map.Icon(
        `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#10B981" stroke="#059669" stroke-width="3"/>
          <circle cx="20" cy="20" r="8" fill="white"/>
        </svg>`,
        { size: { w: 40, h: 40 }, anchor: { x: 20, y: 20 } }
      );
      const driverMarker = new window.H.map.Marker(
        { lat: driverLocation.lat, lng: driverLocation.lng }, 
        { icon: driverIcon }
      );
      map.addObject(driverMarker);
    }
    
    // Add order markers
    orders.forEach(order => {
      if (!order.latitude || !order.longitude) return;
      
      const isSelected = selectedOrder?.id === order.id;
      const isTransit = order.status === 'in_transit';
      const color = isTransit ? '#10B981' : '#3B82F6';
      const size = isSelected ? 36 : 28;
      
      const orderIcon = new window.H.map.Icon(
        `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="${size/2}" cy="${size/2}" r="${size/2-3}" fill="${color}" stroke="white" stroke-width="3"/>
          <rect x="${size/2-5}" y="${size/2-5}" width="10" height="10" rx="2" fill="white"/>
        </svg>`,
        { size: { w: size, h: size }, anchor: { x: size/2, y: size/2 } }
      );
      const marker = new window.H.map.Marker(
        { lat: order.latitude, lng: order.longitude }, 
        { icon: orderIcon }
      );
      map.addObject(marker);
    });
    
    // Center on selected order or driver
    if (selectedOrder?.latitude && selectedOrder?.longitude) {
      map.setCenter({ lat: selectedOrder.latitude, lng: selectedOrder.longitude }, true);
      map.setZoom(15, true);
    } else if (driverLocation?.lat && driverLocation?.lng) {
      map.setCenter({ lat: driverLocation.lat, lng: driverLocation.lng }, true);
    }
  }, [driverLocation, selectedOrder, orders, mapReady]);
  
  return (
    <div ref={mapRef} className="w-full h-full absolute inset-0" />
  );
};

export default function DriverApp() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [driver, setDriver] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [stats, setStats] = useState({ today: 0, earned: 0, rating: 4.9 });
  const [routeInfo, setRouteInfo] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const prevOrderCount = useRef(0);
  
  // Photo capture state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [pendingDeliveryOrder, setPendingDeliveryOrder] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const locationWatchRef = useRef(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  // WebSocket connection for real-time location broadcasting
  useEffect(() => {
    if (!token || !WS_URL || !driver) return;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/driver/${driver.id}`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('Driver WebSocket connected');
          
          // Start watching location and broadcasting
          if (navigator.geolocation && isOnline) {
            locationWatchRef.current = navigator.geolocation.watchPosition(
              (pos) => {
                const location = {
                  lat: pos.coords.latitude,
                  lng: pos.coords.longitude
                };
                setCurrentLocation(location);
                
                // Broadcast location via WebSocket
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({
                    type: 'location_update',
                    latitude: location.lat,
                    longitude: location.lng
                  }));
                }
              },
              (err) => console.log('Location watch error:', err),
              { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
            );
          }
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'location_ack') {
              // Location update acknowledged
            }
          } catch (e) {
            console.error('WebSocket message error:', e);
          }
        };
        
        ws.onclose = () => {
          console.log('Driver WebSocket disconnected');
          if (locationWatchRef.current) {
            navigator.geolocation.clearWatch(locationWatchRef.current);
          }
          // Reconnect after 3 seconds if still online
          if (isOnline) {
            setTimeout(connectWebSocket, 3000);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Driver WebSocket error:', error);
        };
      } catch (e) {
        console.error('Driver WebSocket connection failed:', e);
      }
    };
    
    if (isOnline) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, [token, driver, isOnline]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          setNotificationsEnabled(permission === 'granted');
        });
      }
    }
  }, []);

  // Get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCurrentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.log('Location error:', err),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [personnelRes, ordersRes] = await Promise.all([
        axios.get(`${API}/personnel`, { headers }),
        axios.get(`${API}/orders`, { headers })
      ]);
      
      const currentDriver = personnelRes.data.find(d => d.email === user?.email);
      setDriver(currentDriver);
      
      if (currentDriver) {
        const driverOrders = ordersRes.data.filter(o => 
          o.assigned_driver_id === currentDriver.id && 
          ['assigned', 'in_transit'].includes(o.status)
        );
        
        // Check for new orders and send notification
        if (driverOrders.length > prevOrderCount.current && prevOrderCount.current > 0) {
          const newOrders = driverOrders.length - prevOrderCount.current;
          if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
            new Notification('New Delivery Assigned!', {
              body: `You have ${newOrders} new delivery${newOrders > 1 ? 's' : ''} assigned.`,
              icon: '/favicon.ico',
              tag: 'new-delivery'
            });
          }
          toast.success(`${newOrders} new delivery${newOrders > 1 ? 's' : ''} assigned!`);
        }
        prevOrderCount.current = driverOrders.length;
        
        setOrders(driverOrders);
        
        // Calculate stats (mock data for demo)
        const deliveredToday = ordersRes.data.filter(o => 
          o.assigned_driver_id === currentDriver.id && 
          o.status === 'delivered'
        ).length;
        
        const totalEarned = ordersRes.data
          .filter(o => o.assigned_driver_id === currentDriver.id && o.status === 'delivered')
          .reduce((sum, o) => sum + (o.order_value * 0.15), 0); // 15% commission
        
        setStats({
          today: deliveredToday + driverOrders.length,
          earned: Math.round(totalEarned),
          rating: 4.9
        });
        
        // Set first order as selected if none selected
        if (driverOrders.length > 0 && !selectedOrder) {
          setSelectedOrder(driverOrders[0]);
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, user, logout, navigate, selectedOrder]);

  // Fetch route info for selected order
  useEffect(() => {
    const fetchRouteInfo = async () => {
      if (!selectedOrder || !driver) return;
      
      try {
        const res = await axios.get(`${API}/routes/driver/${driver.id}`, { headers });
        const route = res.data.routes?.find(r => r.order_id === selectedOrder.id);
        if (route) {
          setRouteInfo({
            distance: (route.distance_meters / 1000).toFixed(1),
            duration: Math.round(route.duration_seconds / 60)
          });
        }
      } catch (error) {
        // Use fallback calculation
        if (driver.latitude && driver.longitude && selectedOrder.latitude && selectedOrder.longitude) {
          const R = 6371;
          const dLat = (selectedOrder.latitude - driver.latitude) * Math.PI / 180;
          const dLon = (selectedOrder.longitude - driver.longitude) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(driver.latitude * Math.PI / 180) * Math.cos(selectedOrder.latitude * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          const distance = R * c;
          setRouteInfo({
            distance: distance.toFixed(1),
            duration: Math.round(distance * 2.5) // ~24 km/h average city speed
          });
        }
      }
    };
    
    fetchRouteInfo();
  }, [selectedOrder, driver, token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleStartDelivery = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/status/in_transit`, {}, { headers });
      toast.success('Delivery started!');
      fetchData();
    } catch {
      toast.error('Failed to start delivery');
    }
  };

  // Open photo modal when driver wants to complete delivery
  const handleMarkDelivered = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setPendingDeliveryOrder(order);
    setShowPhotoModal(true);
  };

  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image (JPEG, PNG, or WebP)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be smaller than 10MB');
        return;
      }
      
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // Submit delivery with photo
  const handleSubmitDelivery = async () => {
    if (!selectedPhoto || !pendingDeliveryOrder) {
      toast.error('Please capture or select a delivery proof photo');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedPhoto);
      
      await axios.post(
        `${API}/orders/${pendingDeliveryOrder.id}/complete-delivery`,
        formData,
        { 
          headers: { 
            ...headers, 
            'Content-Type': 'multipart/form-data' 
          } 
        }
      );
      
      toast.success('Delivery completed with proof photo!');
      
      // Clean up
      setShowPhotoModal(false);
      setPendingDeliveryOrder(null);
      setSelectedPhoto(null);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
      setSelectedOrder(null);
      
      fetchData();
    } catch (error) {
      console.error('Failed to complete delivery:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete delivery');
    } finally {
      setIsUploading(false);
    }
  };

  // Cancel photo modal
  const handleCancelPhotoModal = () => {
    setShowPhotoModal(false);
    setPendingDeliveryOrder(null);
    setSelectedPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const handleShowRoute = () => {
    if (selectedOrder) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedOrder.latitude},${selectedOrder.longitude}`;
      window.open(url, '_blank');
    }
  };

  const handleReportIssue = () => {
    toast.info('Issue reporting coming soon');
  };

  const driverLocation = currentLocation || (driver ? { lat: driver.latitude, lng: driver.longitude } : null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-950 flex overflow-hidden" data-testid="driver-app">
      {/* Left Sidebar - Driver Info & Deliveries */}
      <div className="w-[280px] min-w-[280px] bg-gray-900 flex flex-col border-r border-gray-800 h-full overflow-hidden">
        {/* Driver Profile Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-cyan-400 flex items-center justify-center text-gray-900 font-bold text-xl">
              {user?.name?.charAt(0) || 'D'}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-semibold truncate">{user?.name || 'Driver'}</h2>
              <div className="flex items-center gap-1 text-gray-400 text-sm">
                <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                <span>{stats.rating}</span>
                <span className="text-gray-600">·</span>
                <span className="truncate">{driver?.vehicle_type || 'Toyota Camry'} · {driver?.vehicle_plate || 'AU 123'}</span>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { logout(); navigate('/login'); }}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Online Status Toggle */}
          <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-gray-500'}`} />
              <span className="text-white text-sm">
                {isOnline ? 'Online – Ready for deliveries' : 'Offline'}
              </span>
            </div>
            <Switch 
              checked={isOnline} 
              onCheckedChange={setIsOnline}
              className="data-[state=checked]:bg-emerald-500"
            />
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 p-4 border-b border-gray-800">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats.today}</p>
            <p className="text-xs text-gray-500">Today</p>
          </div>
          <div className="text-center border-x border-gray-800">
            <p className="text-2xl font-bold text-emerald-400">${stats.earned}</p>
            <p className="text-xs text-gray-500">Earned</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats.rating}</p>
            <p className="text-xs text-gray-500">Rating</p>
          </div>
        </div>
        
        {/* Onboarding / approval status */}
        <DriverOnboarding driver={driver} token={token} onDone={fetchData} />

        {/* Deliveries Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Deliveries</h3>
          <span className="bg-gray-800 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            {orders.length}
          </span>
        </div>
        
        {/* Deliveries List */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
          {orders.length > 0 ? (
            orders.map(order => (
              <DeliveryCard
                key={order.id}
                order={order}
                isActive={selectedOrder?.id === order.id}
                onSelect={() => setSelectedOrder(order)}
                onStartDelivery={handleStartDelivery}
                onMarkDelivered={handleMarkDelivered}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No active deliveries</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Map Area */}
      <div className="flex-1 relative h-full overflow-hidden">
        <DriverMap 
          driverLocation={driverLocation}
          selectedOrder={selectedOrder}
          orders={orders}
        />
        
        {/* Action Buttons - Top Right */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <Button 
            onClick={handleShowRoute}
            disabled={!selectedOrder}
            className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 gap-2"
            data-testid="show-route-btn"
          >
            <Route className="w-4 h-4" />
            Show Route
          </Button>
          
          <Button 
            onClick={() => selectedOrder && (selectedOrder.status === 'in_transit' 
              ? handleMarkDelivered(selectedOrder.id) 
              : handleStartDelivery(selectedOrder.id))}
            disabled={!selectedOrder}
            className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 gap-2"
            data-testid="complete-btn"
          >
            <CheckCircle className="w-4 h-4" />
            {selectedOrder?.status === 'in_transit' ? 'Complete' : 'Start Delivery'}
          </Button>
          
          <Button 
            onClick={handleReportIssue}
            variant="outline"
            className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 gap-2"
            data-testid="report-issue-btn"
          >
            <AlertTriangle className="w-4 h-4" />
            Report Issue
          </Button>
        </div>
        
        {/* Distance/ETA Panel - Bottom Right */}
        {selectedOrder && routeInfo && (
          <div className="absolute bottom-4 right-4 bg-gray-900/95 backdrop-blur rounded-xl p-4 border border-gray-800 min-w-[180px]">
            <div className="mb-3">
              <p className="text-3xl font-bold text-white">{routeInfo.distance}<span className="text-lg text-gray-400 ml-1">km</span></p>
              <p className="text-gray-500 text-sm">to next stop</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{routeInfo.duration}<span className="text-lg text-gray-400 ml-1">min</span></p>
              <p className="text-gray-500 text-sm">estimated arrival</p>
            </div>
          </div>
        )}
        
        {/* HERE Attribution */}
        <div className="absolute bottom-4 left-4 text-gray-600 text-xs">
          © HERE
        </div>
      </div>
      
      {/* Photo Capture Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="photo-modal">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-800">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Delivery Proof Photo</h3>
              <button 
                onClick={handleCancelPhotoModal}
                className="p-2 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors"
                data-testid="close-photo-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              {/* Order Info */}
              {pendingDeliveryOrder && (
                <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                  <p className="text-emerald-400 font-mono text-sm">#{pendingDeliveryOrder.id.slice(0, 6).toUpperCase()}</p>
                  <p className="text-white font-medium">{pendingDeliveryOrder.customer_name}</p>
                  <p className="text-gray-400 text-sm truncate">{pendingDeliveryOrder.delivery_address}</p>
                </div>
              )}
              
              {/* Photo Preview or Capture Button */}
              {photoPreview ? (
                <div className="relative mb-4">
                  <img 
                    src={photoPreview} 
                    alt="Delivery proof" 
                    className="w-full h-64 object-cover rounded-lg"
                    data-testid="photo-preview"
                  />
                  <button 
                    onClick={() => {
                      setSelectedPhoto(null);
                      URL.revokeObjectURL(photoPreview);
                      setPhotoPreview(null);
                    }}
                    className="absolute top-2 right-2 p-2 bg-red-500/90 hover:bg-red-600 rounded-full text-white"
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-64 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500/50 hover:bg-gray-800/30 transition-colors mb-4"
                  data-testid="photo-capture-area"
                >
                  <Camera className="w-16 h-16 text-gray-600 mb-3" />
                  <p className="text-gray-400 font-medium">Tap to capture or upload photo</p>
                  <p className="text-gray-600 text-sm mt-1">Show the delivered items clearly</p>
                </div>
              )}
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-input"
              />
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleCancelPhotoModal}
                  variant="outline"
                  className="flex-1 bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                  data-testid="cancel-delivery-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitDelivery}
                  disabled={!selectedPhoto || isUploading}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-delivery-btn"
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Complete Delivery
                    </>
                  )}
                </Button>
              </div>
              
              {/* Info Text */}
              <p className="text-gray-500 text-xs text-center mt-4">
                Photo proof is required to complete this delivery
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
